import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Layers,
  Shield,
  Database,
  Server,
  Globe,
  CheckCircle2,
} from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const features = [
  {
    icon: Layers,
    title: "AI Structure Recognition",
    description:
      "Detects headers, sections, tables, and layout hierarchy across varying document formats — without template dependency.",
    highlights: [
      "No template setup required",
      "Multi-format support",
      "Intelligent hierarchy detection",
    ],
  },
  {
    icon: Shield,
    title: "Business Format Governance",
    description:
      "Applies predefined, business-approved templates and formatting standards automatically across regions and departments.",
    highlights: [
      "Centralized format rules",
      "Regional variations handled",
      "Department-specific templates",
    ],
  },
  {
    icon: CheckCircle2,
    title: "Validation & Controls",
    description:
      "Built-in rule enforcement, exception detection, and controlled review workflows.",
    highlights: [
      "Automated rule enforcement",
      "Exception flagging",
      "Review workflow integration",
    ],
  },
  {
    icon: Database,
    title: "Enterprise Integration",
    description:
      "Outputs structured documents ready for ERP, CRM, reporting, and compliance systems. API-first architecture.",
    highlights: ["REST API access", "Webhook support", "Pre-built connectors"],
  },
  {
    icon: Server,
    title: "Scalable Deployment",
    description:
      "Cloud, private cloud, or on-premise deployment with enterprise security controls and audit logging.",
    highlights: [
      "Multi-tenant architecture",
      "Auto-scaling",
      "Full audit trail",
    ],
  },
  {
    icon: Globe,
    title: "Global Operations",
    description:
      "Support for multiple languages, regional document formats, and international compliance requirements.",
    highlights: [
      "Multi-language support",
      "Regional compliance",
      "Time zone aware",
    ],
  },
];

export const Features = () => {
  return (
    <div className="pt-16 md:pt-20" data-testid="features-page">
      {/* Hero Section */}
      <section
        className="section-padding hero-texture min-h-[70vh] flex items-center"
        data-testid="features-hero"
      >
        <div className="container-width relative z-10">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* <p className="label-mono mb-6">Platform Features</p> */}
            <h1
              className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-neutral-950 mb-8"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Built for High-Volume Enterprise Document Control
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-neutral-600">
              Governed AI standardization infrastructure designed for
              enterprises that process thousands of documents daily.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Grid - Bento Style */}
      <section
        className="section-padding bg-neutral-50"
        data-testid="features-grid"
      >
        <div className="container-width">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className={`feature-card ${index === 0 ? "lg:col-span-2 lg:row-span-1" : ""}`}
                variants={fadeInUp}
              >
                <div className="flex items-start justify-between mb-6">
                  <feature.icon className="w-8 h-8 text-neutral-950" />
                  <span className="text-xs font-mono text-neutral-400">
                    0{index + 1}
                  </span>
                </div>

                <h3
                  className="text-2xl font-semibold text-neutral-950 mb-4"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  {feature.title}
                </h3>

                <p className="text-neutral-600 leading-relaxed mb-6">
                  {feature.description}
                </p>

                <ul className="space-y-2">
                  {feature.highlights.map((highlight) => (
                    <li
                      key={highlight}
                      className="flex items-center gap-2 text-sm text-neutral-500"
                    >
                      <span className="w-1 h-1 bg-neutral-400 rounded-full" />
                      {highlight}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Technical Specs Section */}
      <section className="section-padding" data-testid="tech-specs">
        <div className="container-width">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div>
              <p className="label-mono mb-6">Technical Specifications</p>
              <h2
                className="text-3xl md:text-4xl font-semibold tracking-tight text-neutral-950 mb-8"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Enterprise-Grade Architecture
              </h2>
              <div className="space-y-6">
                <div className="border-l-2 border-neutral-200 pl-6">
                  <h4 className="font-semibold text-neutral-950 mb-2">
                    Processing Capacity
                  </h4>
                  <p className="text-neutral-600 text-sm">
                    Up to 10,000 documents per hour with auto-scaling
                    infrastructure
                  </p>
                </div>
                <div className="border-l-2 border-neutral-200 pl-6">
                  <h4 className="font-semibold text-neutral-950 mb-2">
                    Format Support
                  </h4>
                  <p className="text-neutral-600 text-sm">
                    PDF, DOCX, XLSX, Images, Scanned documents, and 50+
                    additional formats
                  </p>
                </div>
                <div className="border-l-2 border-neutral-200 pl-6">
                  <h4 className="font-semibold text-neutral-950 mb-2">
                    API Response Time
                  </h4>
                  <p className="text-neutral-600 text-sm">
                    Average processing time under 2 seconds for standard
                    documents
                  </p>
                </div>
                <div className="border-l-2 border-neutral-200 pl-6">
                  <h4 className="font-semibold text-neutral-950 mb-2">
                    Uptime SLA
                  </h4>
                  <p className="text-neutral-600 text-sm">
                    99.9% availability guarantee with enterprise support
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-neutral-950 text-white p-12">
              <p className="text-xs font-mono uppercase tracking-wider text-neutral-500 mb-6">
                Integration
              </p>
              <h3
                className="text-2xl font-semibold mb-8"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                API-First Architecture
              </h3>
              <div className="font-mono text-sm bg-neutral-900 p-6 rounded-sm mb-6 overflow-x-auto">
                <pre className="text-neutral-300">
                  {`POST /api/v1/documents/normalize
Content-Type: application/json
Authorization: Bearer {api_key}

{
  "document_url": "https://...",
  "output_format": "structured_json",
  "template_id": "procurement_v1"
}`}
                </pre>
              </div>
              <p className="text-neutral-400 text-sm">
                RESTful API with comprehensive documentation and SDKs for
                Python, Node.js, Java, and .NET.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="section-padding bg-neutral-50"
        data-testid="features-cta"
      >
        <div className="container-width">
          <motion.div
            className="text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2
              className="text-3xl md:text-4xl font-semibold tracking-tight text-neutral-950 mb-6"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Ready to see it in action?
            </h2>
            <p className="text-neutral-600 mb-8">
              Schedule a demo to see how Bridge Format AIQ can transform your
              document operations.
            </p>
            <Link
              to="/contact-us"
              className="btn-primary gap-2 btn-hover-scale"
              data-testid="features-demo-btn"
            >
              Request Enterprise Demo
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Features;
